/**
 * AppointmentsTilesWidget — matches the Penpot "Work Orders Section" design:
 * 9 status tiles + 1 "Total Appointments" tile in a 5-col × 2-row grid.
 * Status labels & colors come from the bootstrap catalog's `statuses` array
 * (hydrated from public.status WHERE type='appointment'); the Total tile is
 * derived client-side from the sum of all status counts and uses the green
 * accent from the mockup.
 *
 * Note: the old "$ All Open / $ Partial / $ Completed" financial tiles in
 * the BE response are intentionally NOT rendered here — the updated Penpot
 * dropped them in favor of the single Total tile. They remain in the BE
 * response for any future widget that wants them.
 */

import * as React from "react";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { MetricTile, RefreshButton, TileGrid, WidgetCard } from "components/dashboard";
import { useAppointmentsSummary, formatQueryError } from "../hooks";
import type { LayoutItem, StatusEntry, WidgetConfig } from "../types";

interface Props {
  widget: WidgetConfig;
  layout: LayoutItem;
}

const TOTAL_ACCENT = "34,197,94"; // tailwind green-500 — matches the Penpot total tile

export function AppointmentsTilesWidget({ widget, layout }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useAppointmentsSummary();

  const statuses = (widget.display.statuses as StatusEntry[] | undefined) ?? [];

  // One tile per status, in catalog order (matches calendar pill order via
  // public.status."order"). MetricTile handles label uppercasing.
  const statusTiles = statuses.map((s) => {
    const bucket = data?.statusCounts.find((b) => b.statusId === s.id);
    return { key: `s-${s.id}`, label: s.label, value: bucket?.count ?? 0, color: s.color };
  });

  // Total = sum of every status count. Avoids a separate endpoint call.
  const total = data ? data.statusCounts.reduce((sum, b) => sum + b.count, 0) : 0;

  return (
    <WidgetCard
      title={widget.title}
      w={widget.defaultSize.w}
      h={widget.defaultSize.h}
      x={layout.x}
      y={layout.y}
      loading={isLoading}
      error={error ? formatQueryError(error) : null}
      headerRight={
        <RefreshButton onClick={() => queryClient.invalidateQueries(["dashboard", "appointments-summary"])} />
      }
    >
      <TileGrid cols={5}>
        {statusTiles.map((tile) => (
          <MetricTile
            key={tile.key}
            label={tile.label}
            value={tile.value}
            color={tile.color}
            loading={isLoading}
          />
        ))}
        <MetricTile
          key="total"
          label={t("views.home.dashboard.totalAppointments", "Total Appointments")}
          value={total}
          color={TOTAL_ACCENT}
          loading={isLoading}
        />
      </TileGrid>
    </WidgetCard>
  );
}

